import { StatsTableColumn } from '../enum/TableColumn';
import { CURRENCY_BORDER_COLOR, CURRENCY_LIST_BORDER_COLOR, FILTER_DEPENDENT_COLUMNS } from '../constant/constants';
import { GeoColumns } from '../enum/GeoDivisionColumnName';
import { t } from 'i18next';
import { Column } from '../enum/Column';
import { DeviceColumns } from '../enum/DeviceColumns';
import { PaymentTypeColumn } from '../enum/PaymentType';

/**
 * Returns true if the column is a geo column but not the last one before the currency column (CCY).
 * Used to exclude that special last geo column when applying certain logic.
 */
export function isGeoColumnExcludingLastBeforeCCY(column: string, columns: Column[]): boolean {
  const lastBeforeCurrency = getLastColumnBeforeCurrency(
    columns.map((c) => c.name),
    (col) => col === StatsTableColumn.CCY,
  );
  const geoValues = Object.values(GeoColumns).filter((c) => c !== lastBeforeCurrency);

  return geoValues.includes(column as any);
}

/**
 * Returns true if the column is a device column but not the last one before the currency column (CCY).
 * Used to exclude that special last device column when applying certain logic.
 */
export function isDeviceColumnExcludingLastBeforeCCY(column: string, columns: Column[]): boolean {
  const lastBeforeCurrency = getLastColumnBeforeCurrency(
    columns.map((c) => c.name),
    (col) => col === StatsTableColumn.CCY,
  );

  const deviceValues = Object.values(DeviceColumns).filter((c) => c !== lastBeforeCurrency);

  return deviceValues.includes(column as DeviceColumns);
}

/**
 * Returns true if the column is any geo column.
 */
export function isGeoColumn(column: string): boolean {
  const isGeo = Object.values(GeoColumns).includes(column as GeoColumns);

  return isGeo;
}

/**
 * Returns true if the column is device column.
 */
export function isDeviceColumn(column: string): boolean {
  const isDevice = Object.values(DeviceColumns).includes(column as DeviceColumns);

  return isDevice;
}
/**
 * Returns true if the column is payment type column.
 */
export function isPaymentTypeColumn(column: string): boolean {
  const isPaymentType = Object.values(PaymentTypeColumn).includes(column as PaymentTypeColumn);

  return isPaymentType;
}

/**
 * Returns the last column that appears before the currency column.
 */
export const getLastColumnBeforeCurrency = (columns: string[], isCurrency: (col: string) => boolean): string | null => {
  const currencyIndex = columns.findIndex(isCurrency);

  if (currencyIndex > 0) {
    return columns[currencyIndex - 1];
  }

  return columns.length > 0 ? columns[columns.length - 1] : null;
};

/**
 * Returns the appropriate CSS class for a given table column
 * depending on the rendering context.
 */
export function getTableClass(
  column: string,
  context: 'column' | 'footer' | 'footer_row',
  columns: Column[],
  isMobile: boolean,

  isViewPerCurrency?: boolean,
): string {
  const isCurrency = column === StatsTableColumn.CCY;

  const lastBeforeCurrency = getLastColumnBeforeCurrency(
    columns.map((c) => c.name),
    (col) => col === StatsTableColumn.CCY,
  );
  const currentColumn = columns.find((c) => c.name === column);
  const isPinned = currentColumn?.isPinned ?? true;

  const isLastBeforeCurrency = column === lastBeforeCurrency;

  switch (context) {
    case 'column':
      return getColumnClass(column, isCurrency, isLastBeforeCurrency, isPinned, columns, isMobile);

    case 'footer':
      return getFooterClass(column, isCurrency, isLastBeforeCurrency, columns, isViewPerCurrency);

    case 'footer_row':
      return getFooterRowClass(isCurrency, isLastBeforeCurrency);

    default:
      return '';
  }
}

/* -------------------------------------------------------------------------- */
/*                               Helper Methods                               */
/* -------------------------------------------------------------------------- */

/**
 * Returns class names for regular table columns.
 */
function getColumnClass(
  column: string,
  isCurrency: boolean,
  isLastBeforeCurrency: boolean,
  isPinned: boolean,
  columns: Column[],
  isMobile: boolean,
): string {
  const anyGeoPinned = columns.some((col) => isGeoColumn(col.name) && !col.isPinned);
  const shouldBeSticky =
    (isGeoColumn(column) && isPinned) ||
    (isCurrency && !anyGeoPinned) ||
    (!isGeoColumn(column) && isPinned) ||
    (isDeviceColumn(column) && !anyGeoPinned) ||
    (isPaymentTypeColumn(column) && !anyGeoPinned);

  const baseClass = shouldBeSticky ? 'sticky_column' : 'column';
  const allGeoPinned = columns.filter((col) => isGeoColumn(col.name)).every((col) => col.isPinned);
  const shouldBePinned = allGeoPinned && isDeviceColumn(column);
  const pinnedClass = isPinned || (allGeoPinned && isCurrency) || shouldBePinned ? 'pinned' : '';
  const modifier = isLastBeforeCurrency ? 'last_geo_column' : isCurrency ? 'currency' : '';
  return joinClasses(baseClass, modifier, pinnedClass);
}

/**
 * Returns class names for footer cells.
 */
function getFooterClass(
  column: string,
  isCurrency: boolean,
  isLastBeforeCurrency: boolean,
  columns: Column[],
  isViewPerCurrency?: boolean,
): string {
  // If "view per currency" mode is active → use 'sum_column'
  if (isViewPerCurrency) return 'sum_column';

  // If it's a geo or device column but not the last before CCY → use 'empty_columns'
  if (isGeoColumnExcludingLastBeforeCCY(column, columns) || isDeviceColumnExcludingLastBeforeCCY(column, columns)) {
    return 'empty_columns';
  }

  // If it's the last column before CCY or the currency column → use 'button_column'
  if (isLastBeforeCurrency || isCurrency) return 'button_column';

  // Default
  return 'sum_column';
}

/**
 * Returns class names for footer rows.
 */
function getFooterRowClass(isCurrency: boolean, isLastBeforeCurrency: boolean): string {
  if (isLastBeforeCurrency) return 'last_geo_column';
  if (isCurrency) return 'currency';
  return '';
}

/**
 * Joins multiple CSS class names safely.
 */
function joinClasses(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export const borderStyle = (params: {
  idx?: number; // Optional row index
  dataLength?: number; // Total rows count
  isFull?: boolean; // Always draw top & bottom borders
  isLastColumn?: boolean; // Draw right border
  isCCYColumn?: boolean; // Draw left border for CCY
  isFirstColumnAfterCCY?: boolean; // Draw left border for first column after CCY
  visibleFooter?: boolean;
}): React.CSSProperties => {
  const {
    idx,
    dataLength,
    isFull = false,
    isLastColumn = false,
    isCCYColumn = false,
    isFirstColumnAfterCCY = false,
    visibleFooter,
  } = params;

  const topBorder =
    isFull || idx === 0
      ? `1px solid ${visibleFooter === true ? CURRENCY_LIST_BORDER_COLOR : CURRENCY_BORDER_COLOR}`
      : undefined;

  const bottomBorder =
    isFull || (typeof idx === 'number' && typeof dataLength === 'number' && idx === dataLength - 1)
      ? `1px solid ${visibleFooter ? CURRENCY_LIST_BORDER_COLOR : CURRENCY_BORDER_COLOR}`
      : undefined;

  return {
    borderTop: topBorder,
    borderRight: isLastColumn
      ? `1px solid ${visibleFooter ? CURRENCY_LIST_BORDER_COLOR : CURRENCY_BORDER_COLOR}`
      : undefined,
    borderBottom: bottomBorder,
    borderLeft:
      isCCYColumn || isFirstColumnAfterCCY
        ? `1px solid ${visibleFooter ? CURRENCY_LIST_BORDER_COLOR : CURRENCY_BORDER_COLOR}`
        : undefined,
  };
};

export function getCellClass(column: string, row: Record<string, number>): string {
  const isCurrency = column === StatsTableColumn.CCY;
  const isNumber = typeof row[column] === 'number';
  const actionColumn = column === t('cashRegister.table.header.actions');

  if (isCurrency || isNumber) {
    return 'text_right';
  } else if (actionColumn) {
    return 'image_column';
  } else return 'entity_name';
}

export function isFilterDependentColumn(column: string): boolean {
  return FILTER_DEPENDENT_COLUMNS.has(column);
}

export function isCurrencyColumn(column: string): boolean {
  return column === StatsTableColumn.CCY;
}

export function isUserColumn(column: string): boolean {
  return !isGeoColumn(column) && !isFilterDependentColumn(column) && !isCurrencyColumn(column);
}
